/* tslint:disable:max-file-line-count */
import { CdkDrag } from "@angular/cdk/drag-drop";
import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { BehaviorSubject, Observable } from "rxjs";
import { debounceTime, finalize } from "rxjs/operators";
import { SubSink } from "subsink";
import { LocalService } from "../../../core/storage/local.service";
import { FormService } from "../../../dynamic-forms/form.service";
import { TextboxType } from "../../../dynamic-forms/inputs/textbox/textbox-type.enum";
import { Textbox } from "../../../dynamic-forms/inputs/textbox/textbox.model";
import { ChartService } from "../../../platform/api/chart-lake/chart.service";
import { IChartDocument, IChartMetadata } from "../../../platform/api/chart-lake/charts-list-response.model";
import { LOCALSTORAGE_PAGEAFTERSCREENSYNC } from "../../../platform/modules/member/chase-detail-v2/chase-detail-v2-chart/local-storage-keys";
import { DocumentPage } from "../../../platform/modules/retrieval/retreival-document-review/document-page.model";
import { ArrayHelper } from "../../../utilities/contracts/array-helper";
import { StringHelper } from "../../../utilities/contracts/string-helper";
import { debounceTimeAfterFirst } from "../../../utilities/debounce-time-after";
import { LoaderHelper } from "../../../utilities/loader-helper.class";
import { ORIGINAL_PAGE_HEIGHT } from "../../document/document-constants";
import { CacheTracker } from "../../document/document-page-viewer/cache-tracker.model";
import { DocumentOcrMatch } from "../../document/document-page-viewer/document-ocr-match.model";
import { DocumentPageOcrMatches } from "../../document/document-page-viewer/document-page-ocr-matches.model";
import { OcrWord } from "../../document/document-page-viewer/ocr-word.model";
import { ChartViewerState } from "./chart-viewer-state.model";

@Component({
  selector: "app-chart-page-viewer",
  templateUrl: "./chart-page-viewer.component.html",
  styleUrls: ["./chart-page-viewer.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartPageViewerComponent extends LoaderHelper implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  constructor(
    private localService: LocalService,
    private chartService: ChartService,
    private readonly cd: ChangeDetectorRef,
    private readonly formService: FormService,
    protected readonly zone: NgZone
  ) {
    super(zone);
  }

  get minBuffer(): number {
    return this.isWindowOpen ? this.itemSizeHeight * this.itemSizeHeight : this.itemSizeHeight * 2;
  }

  get maxBuffer(): number {
    return this.isWindowOpen ? this.itemSizeHeight * this.itemSizeHeight : this.itemSizeHeight * 3;
  }

  get pages$(): BehaviorSubject<(DocumentPage | null)[]> {
    return this.state.pages$;
  }

  get pages(): DocumentPage[] {
    return this.state.pages;
  }

  get totalPages(): number {
    return this.state?.totalPages;
  }

  get currentPageNumber(): number {
    // Default to 1 if state or currentPageNumber is undefined/null
    return this.state?.currentPageNumber ?? 1;
  }


  set currentPageNumber(value: number) {
    // Ensure state exists before assigning
    if (this.state) {
      this.state.currentPageNumber = value || 1;
    }

    this.zoomLevel = this.minZoomLevel;

    // Update the form field safely
    if (this.currentPageNumberForm?.get(this.currentPageNumberInput.key)) {
      this.currentPageNumberForm
        .get(this.currentPageNumberInput.key)
        .setValue(this.currentPageNumber);
    }

    this.cd.markForCheck();
  }

  @Input() set chartId(value: number) {
    this.chartId$.next(value);
  }

  get zoomPercentage(): number {
    const minZoomPercentage = this.localService.get(this.localStorageIsWindowOpen, null) === "1" ? 60 : 100;
    const zoomPercentageDelta = 20;
    return minZoomPercentage + (this.zoomLevel * zoomPercentageDelta);
  }

  get zoomStyle(): any {
    const imageSize = document.querySelector("#documentsImg") as HTMLElement;

    // Fallback if image not yet rendered
    const imageWidth = imageSize?.offsetWidth || this.canvasWidth;
    const containerWidth = this.viewport?.elementRef.nativeElement.clientWidth || this.canvasWidth;

    // Center image horizontally
    const leftMargin = (containerWidth - imageWidth) / 2;
    const finalMargin = leftMargin < 0 ? 0 : leftMargin;

    const width = this.isWindowOpen ? "auto" : `${this.zoomPercentage}%`;

    this.itemSizeHeight = this.itemSize * (this.zoomPercentage / 100); // adjust height with zoom

    return {
      width,
      height: `${this.itemSizeHeight}px`,
      margin: `0 ${finalMargin}px`,
      display: "block",
    };
  }

  static chartId: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private currentPageNumber$ = new BehaviorSubject<number>(0);
  @ViewChild(CdkVirtualScrollViewport, { static: true }) private viewport: CdkVirtualScrollViewport;
  @ViewChildren(CdkDrag) private images: QueryList<CdkDrag>;

  private localStorageIsWindowOpen = "isWindowOpen";
  private localStoragePageNumber = "pageNumber";
  private isWindowOpen = this.localService.get(this.localStorageIsWindowOpen, null) === "1";
  private state: ChartViewerState;


  itemSizeHeight = ORIGINAL_PAGE_HEIGHT;
  nextPageClicked = false;
  previousPageClicked = false;
  zoomLevel = 0;
  margin = 0;
  canvasWidth: number;
  ratio: number = this.localService.get(this.localStorageIsWindowOpen, null) === "1" ? 7 : this.zoomPercentage;
  itemSize: number = this.localService.get(this.localStorageIsWindowOpen, null) === "1" ? window.innerHeight : ORIGINAL_PAGE_HEIGHT;
  currentPageNumberForm: FormGroup;
  currentPageNumberInput = new Textbox({ key: "CurrentPageNumber" });
  currentZoomLevel = 1;
  private minZoomLevel = 0.3;
  private maxZoomLevel = 8;
  pageMarginBottom = 0;
  zoomStep = 0.1;
  clearCache = false;

  @Input() chartDocument: IChartDocument;
  @Input() jumpPageNumber = 1;
  @Input() isOcrSearchEnabled = true;

  searchPhraseForm: FormGroup;
  searchPhraseInput: Textbox;
  documentOCRMatch: DocumentOcrMatch;
  isOcrSearchInProgress = false;
  showOcrSearchIcon = true;
  isOcrFocus = false;
  @ViewChild("ocrSearchResults", { static: false }) ocrSearchResults: ElementRef;


  private sink = new SubSink();
  private chartId$ = new BehaviorSubject<number | null>(null);
  private cacheTracker: CacheTracker;

  get hasOcrSearchResults(): boolean {
    return this.documentOCRMatch && this.documentOCRMatch.hasPages;
  }

  get ocrCurrentPage(): DocumentPageOcrMatches {
    return this.hasOcrSearchResults ? this.documentOCRMatch.currentPage : null;
  }

  get ocrDocumentResultsSummary(): string {
    return this.hasOcrSearchResults
      ? `${this.documentOCRMatch.currentOcrPageNumberIndex + 1} / ${this.documentOCRMatch.pages.length}`
      : "";
  }

  get ocrDocumentPageWordsSummary(): string {
    return this.hasOcrSearchResults
      ? `${this.ocrCurrentPage.currentIndex + 1} / ${this.ocrCurrentPage.ocrMatches.length}`
      : "";
  }

  setPage(page: DocumentPage): void {
    this.pages[page.pageNumber - 1] = page;
  }

  @HostListener("window:resize")
  windowResize() {
    if (this.canvasWidth !== this.viewport?.elementRef.nativeElement.clientWidth) {
      this.canvasWidth = this.viewport?.elementRef.nativeElement.clientWidth;
    }
  }

  ngOnInit(): void {
    this.state = new ChartViewerState();

    this.currentPageNumberInput = new Textbox({
      key: "CurrentPageNumber",
      type: TextboxType.NUMBER,
      value: 1,
      disabled: true,
    });

    this.currentPageNumberForm = this.formService.createFormGroup([this.currentPageNumberInput]);

    this.searchPhraseInput = new Textbox({
      key: "SearchPhrase",
      placeholder: "Search",
    });
    this.searchPhraseForm = this.formService.createFormGroup([this.searchPhraseInput]);

    this.sink.add(
      this.chartId$
        .pipe(debounceTime(500))
        .subscribe(chartId => {
          this.initialLoad(chartId);
        }),
      this.currentPageNumber$
        .pipe(debounceTimeAfterFirst(50))
        .subscribe(pageNumber => {
          this.currentPageNumber = pageNumber;

          if (this.images) {
            setTimeout(() => this.images.forEach(a => a.reset()));
          }

          this.tryLoading();
        })
    );
  }

  ngOnDestroy() {
    this.sink.unsubscribe();
    this.cleanCacheAfterNGDestory();
  }

  private processChartPages(chart: any): DocumentPage[] {
    return chart.chartDocument.pages
      .filter(x => x.chartFileImageUrl)
      .sort((a, b) => a.chartPageNumber - b.chartPageNumber)
      .map((page: any) =>
        new DocumentPage({
          pageNumber: page.chartPageNumber,
          image: page.chartFileImageUrl,
          ...page,
        })
      );
  }

  private loadAndSetPages(pageNumbers: number[]): void {
    this.loadSubsequent(pageNumbers).subscribe(chart => {
      const pagesToSet = this.processChartPages(chart);
      this.setPages(pagesToSet);
      this.cleanCache();
    });
  }

  private tryLoading(): void {
    if (this.currentPageNumber <= 0 || this.totalPages <= 0) { return; }

    this.zone.runOutsideAngular(() =>
      setTimeout(() => {
        if (!this.state.shouldLoadMore) { return; }

        const pagesToLoad = this.state.getPagesToLoad();
        if (!ArrayHelper.isAvailable(pagesToLoad)) { return; }
        pagesToLoad.forEach(pageNumber =>
          this.setPage({ pageNumber } as any)
        );
        this.loadAndSetPages(pagesToLoad);
      })
    );
  }


  private cleanCache(): void {
    if (this.cacheTracker?.isLimitReached) {
      this.zone.runOutsideAngular(() => {
        const minTime = this.cacheTracker.currentTime;
        setTimeout(() => {
          for (let i = 0; i < this.pages.length; ++i) {
            if (this.pages[i] != null && this.pages[i].time < minTime) {
              this.pages[i] = null;
            }
          }
        });
      });
    }
  }

  private cleanCacheAfterNGDestory(): void {
    this.clearCache = true;

    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        for (let i = 0; i < this.pages.length; ++i) {
          this.pages[i] = null;
        }
      });
    });

  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.jumpPageNumber) {
      this.gotoPage(this.jumpPageNumber);
    }
  }

  ngAfterViewInit() {
    // now this.viewport is available
    this.viewport.checkViewportSize(); // recalc sizes after zoom or page load
  }


  private setPages(pages: DocumentPage[]): void {
    pages.forEach(this.state.setPage.bind(this));
    this.pages$.next(this.pages);
    this.cd.markForCheck();
  }


  private initialLoad(chartId: number): void {
    const pageCount = this.chartDocument.totalPageCount > 10 ? 10 : this.chartDocument.totalPageCount;
    this.currentPageNumberForm.get(this.currentPageNumberInput.key)?.setValue(pageCount > 0 ? 1 : 0);
    this.chartService.getChartPresignedUrl(chartId, 1, pageCount).subscribe((data: any) => {
      const pagesToSet = [...data.chartDocument.pages].map((page: any, index) => {
        page.image = page.chartFileImageUrl;
        return new DocumentPage({ pageNumber: index + 1, ...page });
      });
      this.setPages(pagesToSet);
      this.state.totalPages = this.chartDocument.totalPageCount;
      this.enablePageNumberInput();
    });
  }

  private loadSubsequent(pages: number[]): Observable<any> {
    const begPage = pages[0];
    const endPage = pages[pages.length - 1];
    const numOfPages = endPage - begPage + 1;

    return this.chartService.getChartPresignedUrl(this.chartId$.getValue(), begPage, numOfPages);
  }

  private enablePageNumberInput(): void {
    this.currentPageNumberForm.get(this.currentPageNumberInput.getMasterKey()).enable();
    this.currentPageNumberInput.disabled = false;
    this.cd.markForCheck();
  }


  trackByIndex(index, item) {
    return index;
  }

  hasPageAndSource(page: DocumentPage): boolean {
    return page && StringHelper.isAvailable(page.image);
  }

  nextPage(): void {
    if (this.currentPageNumber < this.totalPages) {
      this.currentPageNumber++;
      this.viewport?.scrollToIndex(this.currentPageNumber - 1, "smooth");
      this.handlePageNumberState(this.currentPageNumber);
      this.cd.markForCheck();
    }
  }

  prevPage(): void {
    if (this.currentPageNumber > 1) {
      this.currentPageNumber--; // setter will handle state + form update
      this.viewport?.scrollToIndex(this.currentPageNumber - 1, "smooth");
      this.handlePageNumberState(this.currentPageNumber);
      this.cd.markForCheck();
    }
  }


  updatePageMarginBottom(): void {
    this.pageMarginBottom = this.currentZoomLevel <= 1 ? 0 : Math.round(
      (this.currentZoomLevel - 1) * (this.itemSize === ORIGINAL_PAGE_HEIGHT ? 1600 : 1000)
    );
  }

  zoomIn() {
    if (this.zoomLevel < this.maxZoomLevel) {
      this.zoomLevel += this.zoomStep;
      this.updateZoom();
    }
  }

  zoomOut() {
    if (this.zoomLevel > this.minZoomLevel) {
      this.zoomLevel -= this.zoomStep;
      this.updateZoom();
    }
  }

  updateZoom() {
    this.itemSizeHeight = this.itemSize * (this.zoomPercentage / 100);
    if (this.viewport) {
      this.viewport.checkViewportSize();
    }
    this.cd.markForCheck();
  }

  onGotoPage(event: any): void {
    const pageNumber = event.target.valueAsNumber;
    this.gotoPage(pageNumber);
  }

  gotoPage(pageNumber: number): void {
    this.currentPageNumber = pageNumber;
    this.handlePageNumberState(pageNumber);
    this.viewport?.scrollToIndex(this.currentPageNumber);
  }

  private handlePageNumberState(pageNumber: any) {
    // Jump on selected thumbnail page number on second screen
    if (this.localService.get(this.localStorageIsWindowOpen, null) === "1"
      && this.localService.get(this.localStoragePageNumber, null) !== pageNumber) {
      this.jumpPageNumber = Number(pageNumber);
      this.localService.put(LOCALSTORAGE_PAGEAFTERSCREENSYNC.key, pageNumber);
      this.localService.put(this.localStoragePageNumber, pageNumber);
    }
  }

  updateIndex(index): void {
    const pageNumber = index + 1;
    this.currentPageNumberForm.get(this.currentPageNumberInput.key)?.setValue(pageNumber);
    this.currentPageNumber$.next(pageNumber);
  }

  /* #region OCR */
  getWordStyles(word: OcrWord): any {
    const isSelected = this.ocrCurrentPage.ocrMatches[this.ocrCurrentPage.currentIndex].ocrWords.includes(word);
    const width = this.isWindowOpen ? "auto" :
      `${(word.boundingBoxes[1].x - word.boundingBoxes[0].x) * (this.zoomPercentage)}%`;
    const imageSize = document.querySelector("#documentsImg") as HTMLElement;
    const updatedHeigh = Number(this.zoomStyle.height.slice(0, -2));
    if (this.isWindowOpen) {
      const newYHeight = (word.boundingBoxes[1].y - word.boundingBoxes[0].y) * updatedHeigh;
      const newXWidth = (word.boundingBoxes[1].x - word.boundingBoxes[0].x) * imageSize.offsetWidth;
      const newXInit = (word.boundingBoxes[0].x * imageSize.offsetWidth) + this.margin;
      const newYInit = (word.boundingBoxes[0].y * updatedHeigh);
      return {
        width: `${newXWidth}px`,
        height: `${newYHeight}px`,
        top: `${newYInit}px`,
        left: `${newXInit}px`,
        position: "absolute",
        backgroundColor: isSelected ? "#00FF11" : "#00F6FF",
        opacity: "0.5",
      };
    } else {
      return {
        width,
        height: `${(word.boundingBoxes[1].y - word.boundingBoxes[0].y) * updatedHeigh}px`,
        top: `${word.boundingBoxes[0].y * updatedHeigh}px`,
        left: `${word.boundingBoxes[0].x * (this.zoomPercentage)}%`,
        position: "absolute",
        backgroundColor: isSelected ? "#00FF11" : "#00F6FF",
        opacity: "0.5",
      };
    }
  }

  getOcrWords(page: DocumentPage): OcrWord[] {
    const ocrPage = this.getOcrPage(page);
    const phrases = ocrPage == null ? [] : ocrPage.ocrMatches;
    const words = phrases.reduce((acc, phrase) => {
      acc.push(...phrase.ocrWords);
      return acc;
    },                           []);
    return words;
  }

  getOcrPage(page: DocumentPage): DocumentPageOcrMatches {
    const ocrPage = this.hasOcrWords(page) ? this.documentOCRMatch.pages.find(a => a.pageNumber === page.pageNumber) : null;
    return ocrPage;
  }

  hasOcrWords({ pageNumber }: DocumentPage): boolean {
    return this.hasOcrSearchResults && this.documentOCRMatch.pages.find(p => p.pageNumber === pageNumber) != null;
  }

  hasOcrWordsAndDocumentMetaData(page: DocumentPage): boolean {
    return this.hasPageAndSource(page) && this.hasOcrWords(page);
  }

  gotoNextOCRMatchPage(): void {
    this.documentOCRMatch.nextPage();
    this.scrollToOcrCurrentWord();
  }

  gotoPrevOCRMatchPage(): void {
    this.documentOCRMatch.prevPage();
    this.scrollToOcrCurrentWord();
  }

  gotoNextOCRMatchPageWord(): void {
    if (this.hasOcrSearchResults) {
      this.ocrCurrentPage.nextWord();
      this.scrollToOcrCurrentWord();
    }
  }

  gotoPrevOCRMatchPageWord(): void {
    if (this.hasOcrSearchResults) {
      this.ocrCurrentPage.prevWord();
      this.scrollToOcrCurrentWord();
    }
  }

  scrollToOcrCurrentWord() {
    const offset = this.ocrCurrentPage.offset(this.itemSizeHeight);
    this.viewport?.scrollToOffset(offset);
  }

  onOcrSearchPhraseFocus(input: HTMLElement, ocr: HTMLElement): void {
    this.isOcrFocus = true;
    const { left, bottom } = input.getBoundingClientRect();
    ocr.style.top = `${bottom - 2}px`;
    ocr.style.left = `${left}px`;
  }

  preventHide(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  showElement(element: HTMLElement): void {
    element.hidden = false;
  }

  fetchOcrMatches(ocr: HTMLElement): void {
    this.isOcrSearchInProgress = true;
    this.documentOCRMatch = null;
    const searchPhrase = this.searchPhraseForm.get(this.searchPhraseInput.key).value;
    this.showElement(ocr);

    this.chartService
      .getChartOCRMatches(this.chartId$.getValue().toString(), searchPhrase)
      .pipe(finalize(() => {
        this.isOcrSearchInProgress = false;
        this.cd.markForCheck();
      }))
      .subscribe(data => {
        this.documentOCRMatch = data;
        this.documentOCRMatch.firstViewed = false;
        const hasPages = ArrayHelper.isAvailable(this.documentOCRMatch.pages);
        this.documentOCRMatch.currentOcrPageNumberIndex = hasPages ? 0 : -1;
        if (hasPages) {
          this.scrollToOcrCurrentWord();
          this.showOcrSearchIcon = false;
        }
        this.cd.markForCheck();
      });
    this.cd.markForCheck();
  }

  resetOcrSearchInput(): void {
    this.searchPhraseForm.get(this.searchPhraseInput.key).reset();
    this.clearOcrSearch();
  }

  private clearOcrSearch(): void {
    this.documentOCRMatch = null;
    if (this.ocrSearchResults) {
      this.ocrSearchResults.nativeElement.hidden = true;
    }
    this.showOcrSearchIcon = true;
  }
  /* #endregion OCR */

}

